import { Component, DestroyRef, OnDestroy, OnInit, PLATFORM_ID, inject, NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { EditGoalsComponent } from '../../../../../components/organization/campaigns/edit-goals/edit-goals.component';
import { FailedRecipientsComponent } from '../../../../../components/organization/campaigns/failed-recipients/failed-recipients.component';
import { StatusComponent } from '../../../../../components/ui/status/status.component';
import { SmsTextComponent } from '../../../../../components/messages/sms-text/sms-text.component';
import { DeliveryComponent } from '../../../../../components/messages/delivery/delivery.component';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { CampaignsService } from '../../../../../services/campaign/campaigns-api.service';
import {
  CampaignInsightsService,
  MessageMeta,
} from '../../../../../services/campaign/campaign-insights.service';
import {
  DeliverySummary,
  deliveredShare,
  formatShare,
} from '../../../../../services/message/delivery.service';
import {
  CampaignDetails,
  CampaignStats,
  EngagementMessage,
  MessagesReport,
} from '../../types/campaignDetails';
import { formatCount, formatCountdown, smsParts } from '../../../../../../utils/sms';
import {
  CampaignSend,
  campaignSends,
  displayMessages,
  isStopped,
  listJoin,
  messageDelivery,
  nextSend,
  orderedMessages,
  plural,
  subjectOf,
} from '../campaign-sends';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

/** How many past sends get their own delivery counts (three requests each). */
const SEND_OUTCOME_LIMIT = 12;
/** Longer timelines show the sends around now until expanded. */
const TIMELINE_PREVIEW = 8;

interface MessageRow {
  message: EngagementMessage;
  subject: string;
  copy: string;
  from: string;
  parts: string;
  sends: string;
  deliveryStatus: string;
  sentAt: Date | null;
}

interface AudienceGroup {
  label: string;
  names: string[];
}

/**
 * Campaign overview: did it get through (delivery outcome), what goes out
 * next, every send in order with how it went, and who and what it is for.
 */
@Component({
  selector: 'app-overview',
  standalone: true,
  imports: [
    RouterLink,
    MatTooltipModule,
    EditGoalsComponent,
    FailedRecipientsComponent,
    StatusComponent,
    SmsTextComponent,
    DeliveryComponent,
    HasPermissionDirective,
  ],
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.css',
})
export class OverviewComponent implements OnInit, OnDestroy {
  private ngZone = inject(NgZone);
  campaignsService = inject(CampaignsService);
  private insights = inject(CampaignInsightsService);
  private destroyRef = inject(DestroyRef);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  campaign: CampaignDetails | null = null;
  campaignStats: CampaignStats | null = null;

  messages: MessageRow[] = [];
  sends: CampaignSend[] = [];
  next: CampaignSend | null = null;
  goals: { id: number; name: string | null; engagementCampaigns: any[] }[] = [];
  audience: AudienceGroup[] = [];
  subjects: Record<string, string> = {};
  messageUuids: string[] = [];

  outcome: DeliverySummary | null = null;
  outcomeState: LoadState = 'idle';
  /** Delivery counts per send, by the report's schedule uuid. */
  sendOutcomes: Record<string, DeliverySummary | 'loading' | 'error'> = {};

  showAllSends = false;
  showEditGoals = false;
  now = new Date();

  readonly formatCount = formatCount;
  readonly plural = plural;

  private reports: MessagesReport[] = [];
  /** Each message's own details (uuid, sender, status), which the campaign leaves out. */
  private meta: Record<number, MessageMeta> = {};
  private metaKey = '';
  private metaReady = false;
  private outcomeKey = '';
  private reportsKey = '';
  private outcomeSub?: Subscription;
  private reportsSub?: Subscription;
  private metaSub?: Subscription;
  private sendSubs: Subscription[] = [];
  private clock?: ReturnType<typeof setInterval>;

  get loaded(): boolean {
    return !!this.campaign?.id;
  }

  get statsReady(): boolean {
    return this.campaignStats?.recipientsCount !== undefined && this.campaignStats?.recipientsCount !== null;
  }

  get sentCount(): number {
    return this.sends.filter((send) => send.past).length;
  }

  get anySent(): boolean {
    return this.sentCount > 0;
  }

  get share(): string {
    return this.outcome ? formatShare(deliveredShare(this.outcome)) : '';
  }

  /** "delivered so far" while receipts are still coming in. */
  get shareLabel(): string {
    return this.outcome && this.outcome.inFlight > 0 ? 'delivered so far' : 'delivered';
  }

  get outcomeBreakdown(): string {
    if (!this.outcome) return '';
    const { total, delivered, failed, inFlight } = this.outcome;
    const parts = [`${formatCount(delivered)} delivered`];
    if (failed) parts.push(`${formatCount(failed)} failed`);
    if (inFlight) parts.push(`${formatCount(inFlight)} with no receipt yet`);
    return `${parts.join(', ')}, of ${formatCount(total)} messages sent`;
  }

  get countdown(): string {
    return this.next ? formatCountdown(this.next.at, this.now) : '';
  }

  /** The timeline rows to draw: all of them, or the sends around now. */
  get visibleSends(): CampaignSend[] {
    if (this.showAllSends || this.sends.length <= TIMELINE_PREVIEW) return this.sends;
    const past = this.sends.filter((send) => send.past);
    const upcoming = this.sends.filter((send) => !send.past);
    const half = TIMELINE_PREVIEW / 2;
    const later = upcoming.slice(0, Math.max(half, TIMELINE_PREVIEW - past.length));
    const earlier = past.slice(-Math.max(half, TIMELINE_PREVIEW - later.length));
    return [...earlier, ...later];
  }

  get hiddenSends(): number {
    return this.sends.length - this.visibleSends.length;
  }

  /**
   * What follows the recipient count: " in 2 organizations and 3 tags." or
   * ". No recipient groups on this campaign." Without the count it is a
   * sentence of its own.
   */
  get audienceSentence(): string {
    const groups = listJoin(
      this.audience.filter((group) => group.names.length).map((group) => group.label)
    );
    if (this.statsReady) {
      return groups ? ` in ${groups}.` : '. No recipient groups on this campaign.';
    }
    return groups ? `In ${groups}.` : 'No recipient groups on this campaign.';
  }

  ngOnInit() {
    this.campaignsService.campaignDetails
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.campaign = data as CampaignDetails;
        this.refresh();
      });

    // assign data to component campaignStat variable
    this.campaignsService.campaignStats
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.campaignStats = data as CampaignStats;
      });

    // Countdowns and "past or upcoming" move with the clock.
    if (this.isBrowser) {
      // Outside the zone, so a pending timer never keeps the app from settling.
      this.clock = this.ngZone.runOutsideAngular(() =>
        setInterval(() => this.ngZone.run(() => this.tick()), 30_000)
      );
    }
  }

  ngOnDestroy() {
    if (this.clock) clearInterval(this.clock);
    this.outcomeSub?.unsubscribe();
    this.reportsSub?.unsubscribe();
    this.metaSub?.unsubscribe();
    this.sendSubs.forEach((sub) => sub.unsubscribe());
  }

  /** Everything the page shows, worked out again whenever the campaign changes. */
  private refresh() {
    const campaign = this.campaign;
    if (!campaign?.id) {
      this.messages = [];
      this.sends = [];
      this.next = null;
      this.goals = [];
      this.audience = [];
      return;
    }

    this.goals = (campaign.engagementObjectives ?? []).map((goal) => ({
      id: goal.id,
      name: goal.name,
      engagementCampaigns: goal.engagementCampaigns,
    }));

    this.audience = [
      {
        label: plural(campaign.engagementOrganizations?.length ?? 0, 'organization', 'organizations'),
        names: (campaign.engagementOrganizations ?? []).map((group) => group?.name ?? '').filter(Boolean),
      },
      {
        label: plural(campaign.engagementRecipientTags?.length ?? 0, 'tag', 'tags'),
        names: (campaign.engagementRecipientTags ?? []).map((group) => group?.name ?? '').filter(Boolean),
      },
      {
        label: plural(campaign.engagementRecipientSegments?.length ?? 0, 'segment', 'segments'),
        names: (campaign.engagementRecipientSegments ?? []).map((group) => group?.name ?? '').filter(Boolean),
      },
    ];

    this.loadReports();
    this.loadMeta();
    this.applyMeta();
  }

  /** Each message's own GET: the uuid every delivery view keys on, its sender and status. */
  private loadMeta() {
    const ids = orderedMessages(this.campaign).map((message) => message.id);
    const key = ids.join(',');
    if (key === this.metaKey) return;
    this.metaKey = key;
    this.metaReady = false;
    this.metaSub?.unsubscribe();
    this.metaSub = this.insights.messageMeta(ids).subscribe((meta) => {
      this.meta = meta;
      this.metaReady = true;
      this.applyMeta();
    });
  }

  /** Everything that needs the messages' uuids: failed-row subjects, sends, delivery. */
  private applyMeta() {
    if (!this.campaign?.id) return;
    const messages = displayMessages(this.campaign, this.meta);
    this.messageUuids = messages.map((message) => message.uuid ?? '').filter(Boolean);
    this.subjects = Object.fromEntries(
      messages.filter((message) => !!message.uuid).map((message) => [message.uuid!, subjectOf(message)])
    );
    this.rebuildSends();
    this.loadOutcome();
  }

  /**
   * Clock tick: refresh countdowns, and only when a send has just gone out,
   * the message rows and the outcome (so delivery cells do not reload).
   */
  private tick() {
    if (!this.campaign?.id) return;
    const sentBefore = this.sentCount;
    this.now = new Date();
    this.sends = campaignSends(this.campaign, this.reports, this.now, this.meta);
    this.next = nextSend(this.sends);
    if (this.sentCount !== sentBefore) {
      this.rebuildSends();
      this.loadOutcome();
    }
  }

  private rebuildSends() {
    if (!this.campaign?.id) return;
    this.sends = campaignSends(this.campaign, this.reports, this.now, this.meta);
    this.next = nextSend(this.sends);

    this.messages = displayMessages(this.campaign, this.meta).map((message) => {
      const copy = String(message.content || message.messageTemplate || '');
      const count = copy ? smsParts(copy) : null;
      const sends = this.sends.filter((send) => send.message.id === message.id);
      const sent = sends.filter((send) => send.past).length;
      const delivery = messageDelivery(message, this.now);
      return {
        message,
        subject: subjectOf(message),
        copy,
        from: String(message.from ?? '').trim(),
        parts: !count?.parts ? '' : count.parts === 1 ? '1 part' : `${count.parts} parts`,
        sends: !sends.length
          ? 'No sends scheduled'
          : sent === 0
          ? `${plural(sends.length, 'send', 'sends')} scheduled`
          : sent === sends.length
          ? sends.length === 1
            ? 'Sent'
            : `All ${formatCount(sends.length)} sends out`
          : `${formatCount(sent)} of ${plural(sends.length, 'send', 'sends')} out`,
        deliveryStatus: delivery.status,
        sentAt: delivery.sentAt,
      };
    });

    this.loadSendOutcomes();
  }

  /** The campaign report links each send to its delivery receipts. */
  private loadReports() {
    const uuid = this.campaign?.uuid ?? '';
    if (!uuid || uuid === this.reportsKey) return;
    this.reportsKey = uuid;
    this.reportsSub?.unsubscribe();
    this.reportsSub = this.insights.messageReports(uuid).subscribe({
      next: (reports) => {
        this.reports = reports;
        this.rebuildSends();
      },
      error: () => {
        // Without the report the timeline still shows each send and its status.
        this.reports = [];
      },
    });
  }

  private loadOutcome() {
    const uuid = this.campaign?.uuid ?? '';
    if (!uuid) return;
    // The fallback sums and "ever completed" need each message's own details.
    if (!this.metaReady) {
      if (this.anySent) {
        this.outcomeState = 'loading';
        this.outcomeKey = ''; // so the call after the details arrive always loads
      }
      return;
    }
    const key = `${uuid}|${this.messageUuids.join(',')}|${this.anySent}`;
    if (key === this.outcomeKey) return;
    this.outcomeKey = key;
    this.outcomeSub?.unsubscribe();

    const everCompleted = displayMessages(this.campaign, this.meta).some((message) =>
      /^completed$/i.test(message.status ?? '')
    );
    if (!this.anySent && !everCompleted) {
      this.outcome = null;
      this.outcomeState = 'idle';
      return;
    }

    this.outcomeState = 'loading';
    this.outcomeSub = this.insights.campaignOutcome(uuid, this.messageUuids).subscribe({
      next: (summary) => {
        this.outcome = summary;
        this.outcomeState = 'ready';
      },
      error: () => {
        this.outcomeState = 'error';
      },
    });
  }

  retryOutcome() {
    this.outcomeKey = '';
    this.loadOutcome();
  }

  /** Delivery counts for the most recent past sends that the report links to. */
  private loadSendOutcomes() {
    const wanted = this.sends
      .filter((send) => send.past && !!send.report?.scheduleUuid)
      .slice(-SEND_OUTCOME_LIMIT)
      .map((send) => send.report!.scheduleUuid);

    for (const uuid of wanted) {
      if (this.sendOutcomes[uuid]) continue;
      this.sendOutcomes = { ...this.sendOutcomes, [uuid]: 'loading' };
      this.sendSubs.push(
        this.insights.sendOutcome(uuid).subscribe({
          next: (summary) => (this.sendOutcomes = { ...this.sendOutcomes, [uuid]: summary }),
          error: () => (this.sendOutcomes = { ...this.sendOutcomes, [uuid]: 'error' }),
        })
      );
    }
  }

  // ── template helpers ──

  sendOutcome(send: CampaignSend): DeliverySummary | 'loading' | 'error' | null {
    const uuid = send.report?.scheduleUuid;
    return uuid ? this.sendOutcomes[uuid] ?? null : null;
  }

  asSummary(value: DeliverySummary | 'loading' | 'error' | null): DeliverySummary | null {
    return value && typeof value === 'object' ? value : null;
  }

  sendShare(summary: DeliverySummary): string {
    return formatShare(deliveredShare(summary));
  }

  /** The report's own delivery rate, when no receipt counts are loaded. */
  reportRate(send: CampaignSend): string {
    const rate = send.report?.deliveryRate;
    const recipients = send.report?.totalRecipient;
    if (rate === null || rate === undefined || !recipients) return '';
    return `${Math.round(Number(rate))}% delivered`;
  }

  countdownOf(send: CampaignSend): string {
    return formatCountdown(send.at, this.now);
  }

  stopped(send: CampaignSend): boolean {
    return isStopped(send.status);
  }

  groupNames(group: AudienceGroup): string {
    const shown = group.names.slice(0, 3).join(', ');
    const more = group.names.length - 3;
    return more > 0 ? `${shown} and ${formatCount(more)} more` : shown;
  }
}
